import type { ActionsNetwork } from 'shared/store/actions'
import { fetchPageData } from 'shared/store/actions'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type {
    BirthdayFormData,
    FormBirthdayBooking,
    MetaShortDTO,
    RestaurantShortDTO,
    OrderRestaurantShortDTO
} from 'shared/types'
import type { Dispatch } from 'redux'
import { errors } from 'shared/utils'

type ActionBirthdayPageDataResponse = {
    type: 'BIRTHDAY_PAGE_RESPONSE',
    data: {
        metaData: MetaShortDTO,
        formData: BirthdayFormData
    }
}

type ActionBirthdaySendFormError = {
    type: 'BIRTHDAY_SEND_FORM_ERROR',
    sendFormError: any
}

type ActionBirthdaySendFormSuccess = {
    type: 'BIRTHDAY_SEND_FORM_SUCCESS'
}

type ActionBirthdayReset = {
    type: 'BIRTHDAY_RESET_SUCCESS'
}

type ActionBirthdaySelectRestaurant = {
    type: 'BIRTHDAY_SELECT_RESTAURANT',
    selectedRestaurant: RestaurantShortDTO
}

type ActionBirthdayRestaurantSearchResults = {
    type: 'SEARCH_RESULTS',
    restaurants: Array<OrderRestaurantShortDTO>
}

type ActionBirthdayRestaurantClearSearch = {
    type: 'CLEAR_SEARCH'
}

type ActionBirthdayError = {
    type: 'BIRTHDAY_ERROR',
    error: any
}

const fetchBirthdayPageData = () =>
    fetchPageData(endpoints.pages.birthday.page, 'BIRTHDAY_PAGE_RESPONSE')

const selectRestaurant = (
    selectedRestaurant: RestaurantShortDTO
): ActionBirthdaySelectRestaurant => ({
    type: 'BIRTHDAY_SELECT_RESTAURANT',
    selectedRestaurant
})

const sendBirthdayForm = (form: FormBirthdayBooking) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.post(endpoints.pages.birthday.page, form)
            dispatch(actionSendFormSuccess())
        } catch (error) {
            const { response } = error
            console.error('Error sending birthday form', response)
            let message = 'form.error.default'
            if (response) {
                const { data } = response
                if (data) {
                    switch (data.message) {
                        case errors.RESTAURANT_NOT_FOUND:
                            message = 'form.error.restaurant.not.found'
                            break
                        case errors.CANNOT_PARSE_RESERVATION_DATE:
                            message =
                                'birthday.form.send.error.reservation.date.wrong.format'
                            break
                        case errors.RESERVATION_DATE_INVALID:
                            message =
                                'birthday.form.send.error.reservation.date.invalid'
                            break
                        case errors.MAX_KIDS_REACHED:
                            message = 'birthday.form.send.error.guests'
                            break
                        case errors.GIFT_NOT_FOUND:
                            message = 'birthday.form.send.error.gift'
                            break
                        case errors.CAKE_NOT_FOUND:
                            message = 'birthday.form.send.error.cake'
                            break
                        case errors.CANNOT_PARSE_BIRTHDATE:
                            message = 'form.error.birthdate.wrong.format'
                            break
                        case errors.BIRTHDATE_INVALID:
                            message = 'form.error.birthdate.invalid'
                            break
                        case errors.INVALID_EMAIL:
                            message = 'form.error.regex.email'
                            break
                        case errors.PHONE_WRONG_FORMAT:
                            message = 'form.error.phone.wrong.format'
                            break
                        case errors.SERVICE_NOT_AVAILABLE_IN_RESTAURANT:
                            message = 'birthday.form.send.error.service'
                            break
                        case errors.CGU_NOT_ACCEPTED:
                            message = 'birthday.form.send.error.cgu'
                            break
                        case errors.TOO_MUCH_CHARACTERS:
                            message = 'birthday.form.send.error.cgu'
                            break
                        default:
                            message = 'form.error.default'
                            break
                    }
                }
            }
            dispatch(actionSendFormError(message))
        }
    }
}

const actionSendFormSuccess = (): ActionBirthdaySendFormSuccess => ({
    type: 'BIRTHDAY_SEND_FORM_SUCCESS'
})

const actionSendFormError = (
    sendFormError: any
): ActionBirthdaySendFormError => ({
    type: 'BIRTHDAY_SEND_FORM_ERROR',
    sendFormError
})

const resetBirthday = (error: any): ActionBirthdayReset => ({
    type: 'BIRTHDAY_RESET_SUCCESS',
    error
})

const resetError = (error: any): ActionBirthdayError => ({
    type: 'BIRTHDAY_ERROR',
    error
})

const fetchOpeningHours = (restaurantId: string) => {
    return async () => {
        try {
            const response = await ws.get(
                `${endpoints.pages.birthday.openings}/${restaurantId}`
            )
            return response.data
        } catch (error) {
            console.error(
                'Error, could not get opening hours from the birthday page for the selected restaurant'
            )
        }
    }
}

export {
    fetchBirthdayPageData,
    sendBirthdayForm,
    selectRestaurant,
    resetBirthday,
    resetError,
    fetchOpeningHours
}

export type Action =
    | ActionBirthdayPageDataResponse
    | ActionBirthdaySelectRestaurant
    | ActionBirthdayRestaurantSearchResults
    | ActionBirthdayRestaurantClearSearch
    | ActionBirthdaySendFormError
    | ActionBirthdaySendFormSuccess
    | ActionBirthdayError
    | ActionBirthdayReset
    | ActionsNetwork
